import React from "react";
import * as yup from "yup";
import memoizeOne from "memoize-one";
import { Formik, FormikActions } from "formik";
import classNames from "classnames";
import TextInput from "components/shared/ui/form-elements/TextField";
import { Link } from "react-router-dom";
import Button from "components/shared/ui/button/Button";
import PasswordTextInput from "components/shared/ui/form-elements/PasswordTextField";
import * as ROUTES from "constants/routes";
import styles from "./AuthForm.module.scss";
import { cleanErrorMsg } from "../../../utils/cleanErrorMsg";
import InputFeedback from "../../shared/ui/form-elements/InputFeedback";

const formInitialValues = {
  email: "",
  password: ""
};

type TFormValues = typeof formInitialValues;
type TFormAction = FormikActions<TFormValues>;

interface Props {
  error: string | null;
  onSubmit: (email: string, password: string) => void;
}

interface State {
  formValues: TFormValues;
  showPassword: boolean;
}

export class LoginForm extends React.Component<Props, State> {
  public state: State = {
    formValues: formInitialValues,
    showPassword: false
  };

  public validationScheme = memoizeOne(() => {
    return yup.object().shape({
      email: yup
        .string()
        .required("Please enter an email address")
        .email("Please enter a valid email address"),
      password: yup.string().required("Please enter a password")
    });
  });

  public submit = async (
    values: TFormValues,
    { setSubmitting }: TFormAction
  ) => {
    await this.props.onSubmit(values.email, values.password);
    setSubmitting(false);
  };

  public render() {
    const { error } = this.props;
    const { formValues } = this.state;
    return (
      <>
        <h1 className={classNames(styles.title, styles.mb)}>
          <span className={styles.titleMain}>Sign in</span> or{" "}
          <Link to={ROUTES.SIGN_UP} className={styles.titleLink}>
            Sign Up
          </Link>
        </h1>

        <Formik
          initialValues={formValues}
          isInitialValid
          validationSchema={this.validationScheme()}
          onSubmit={this.submit}
        >
          {formProps => (
            <form
              className={styles.form}
              onSubmit={formProps.handleSubmit}
              noValidate
            >
              <TextInput
                required
                autoFocus
                placeholder={"Enter your email"}
                label="Email"
                name="email"
                autoComplete="current-user"
                error={formProps.submitCount > 0 && formProps.errors.email}
                touched={!!formProps.touched.email}
                value={formProps.values.email}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
              />

              <PasswordTextInput
                className={styles.formRow}
                required
                placeholder={"Enter password"}
                label="Password"
                name="password"
                autoComplete="current-password"
                error={formProps.submitCount > 0 && formProps.errors.password}
                touched={!!formProps.touched.password}
                value={formProps.values.password}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
              />

              {error && (
                <InputFeedback
                  error={cleanErrorMsg(error)}
                  visible={!!error}
                  classNames={styles.formError}
                />
              )}

              <div className={styles.actions}>
                <Link className={styles.actionLink} to={ROUTES.PASSWORD_FORGET}>
                  Forgot password?
                </Link>
                <div />
                <Button type="submit" disabled={formProps.isSubmitting} accent>
                  {formProps.isSubmitting ? "Signing In" : "Sign In"}
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </>
    );
  }
}

export default LoginForm;
